import LZString from "lz-string";

// Product cache expiry time in milliseconds (default: 1 minute)
const PRODUCT_CACHE_EXPIRY_MS = 1 * 60 * 1000;

const VESSI_GLOBAL_STOREFRONT = "vessi-footwear.myshopify.com";
const VESSI_CANADA_STOREFRONT = "vessi-footwear-cad.myshopify.com";

// CART FUNCTIONS
const cartFunctions = {
  /**
   * Dispatches a custom event to notify about a cart update and opens the cart drawer.
   *
   * @param {object} data - The cart data to include in the event.
   * @param {object} data.cart - The updated cart object.
   * @param {number} data.status - The status code of the cart update (default is 200).
   *
   * This function performs the following steps:
   * 1. Logs the cart data to the console.
   * 2. Checks if the provided data is valid; if not, it exits early.
   * 3. Creates a custom event named 'cart_request' with the provided cart data.
   * 4. Opens the cart drawer by calling `cartFunctions.openCart()`.
   * 5. Dispatches the custom event to notify other parts of the application about the cart update.
   */
  dispatchCartEvent: (data) => {
    if (!data) return;

    const event = new CustomEvent("cart_request", {
      detail: {
        cart: data,
        status: 200,
      },
    });
    document.dispatchEvent(event);
    cartFunctions.updateCartCount(data.item_count);
    cartFunctions.openCart();
  },
  /**
   * Adds an item to the cart.
   *
   * @async
   * @param {object} variant - The variant object to add to the cart.
   * @param {object} [cartProperties={}] - Optional cart properties.
   * @param {string[]} [cartProperties.tags=[]] - Tags associated with the item.
   * @param {string} [cartProperties.notes_message=""] - Notes message for the item.
   * @param {boolean} [cartProperties.vip=false] - Indicates if the item is for VIP customers.
   * @param {string} [cartProperties.theme_tag_final_sale=""] - Theme tag for final sale items.
   * @param {object} [cartProperties.otherProperties={}] - Additional properties for the item.
   * @param {number} [cartProperties.cart_quantity=1] - Quantity of the item to add.
   * @param {string} [cartProperties.giving_price_line_item=null] - Giving price line item.
   * @param {number[]} [cartProperties.giftWithPurchase=null] - Gift with purchase variant IDs.
   * @param {boolean} [cartProperties.has_gift_with_purchase=false] - Indicates if the item has a gift with purchase.
   * @param {number} [quantity=1] - Quantity of the item to add.
   * @returns {Promise<object|null>} - A promise that resolves with the cart data, or null on error.
   */
  addItemToCart: async (
    variant,
    cartProperties = {
      tags: [],
      notes_message: "",
      vip: false,
      theme_tag_final_sale: "",
      otherProperties: {},
      cart_quantity: 1,
      giving_price_line_item: null,
      giftWithPurchase: null,
      has_gift_with_purchase: false,
    },
    quantity = 1
  ) => {
    let {
      tags,
      notes_message,
      vip,
      theme_tag_final_sale,
      otherProperties,
      cart_quantity,
      giving_price_line_item,
      giftWithPurchase,
      has_gift_with_purchase,
    } = cartProperties;

    const variant_id = variant?.id;

    if (!variant_id) {
      console.error("Variant ID is required to add item to cart");
      return null;
    }

    if (variant?.variantAdditionalNotes && !notes_message) {
      notes_message = variant?.variantAdditionalNotes;
    }

    const isFinalSale = tags.includes("Final Sale");
    const isExchangeOnly = tags.includes("exchange_only");
    const isVipFinalSaleItem = vip && tags.includes("VIP-FINALSALE");
    const isThemeFinalSaleItem = tags.includes(theme_tag_final_sale);

    const lineItemProperty =
      isFinalSale || isVipFinalSaleItem || isThemeFinalSaleItem
        ? "Final Sale"
        : isExchangeOnly
          ? "exchange_only"
          : "";

    let messageSuffix = "";
    if (lineItemProperty) {
      messageSuffix = `${lineItemProperty
        .replace("_", " ")
        .replace("exchange only", "Exchange Only")}`;
    }

    if (isThemeFinalSaleItem && !notes_message) {
      notes_message = messageSuffix;
    } else if (!notes_message && messageSuffix) {
      notes_message = messageSuffix;
    }

    const properties = {
      Notes: notes_message,
      _LPROP: lineItemProperty,
      _VIP: vip ? "Exclusive Access" : "",
      ...otherProperties,
    };

    if (giving_price_line_item) {
      properties._GIVING_WEEK = giving_price_line_item;
      if (giving_price_line_item === "DONATE_10") {
        properties["Giving Week"] = "Donate $10";
      } else if (giving_price_line_item === "DONATE_5") {
        properties["Giving Week"] = "Donate $5";
      } else if (giving_price_line_item === "DONATE_0") {
        properties["Giving Week"] = "No Donation";
      }
    }

    const payload = {
      items: [
        { id: variant_id, quantity: cart_quantity || quantity, properties },
      ],
    };

    if (has_gift_with_purchase && giftWithPurchase) {
      giftWithPurchase.forEach((el) => {
        payload.items.push({
          id: el,
          quantity: 1,
          properties: { Notes: "Final Sale" },
        });
      });
    }

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          attributes: {
            _channel: "website",
          },
        }),
      });
      if (!response.ok) {
        // Try to parse error message, but let catch handle toast
        let errorMessage = `Unable to add item to cart.`;
        try {
          const errorData = await response.json();
          errorMessage = errorData?.description || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }
      const data = await response.json();
      cartFunctions.dispatchCartEvent(data);
      return data;
    } catch (error) {
      const message = error?.message || "Unable to add item to cart.";
      toastMessage(message, 5000, "error");
      console.error("Error adding item to cart:", error);
      throw error;
    }
  },
  /**
   * Fetches the current cart data.
   *
   * @async
   * @returns {Promise<object|null>} - A promise that resolves with the cart data, or null on error.
   */
  fetchCart: async () => {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      cartFunctions.dispatchCartEvent(cart);
      return cart;
    } catch (error) {
      console.error("Error fetching cart:", error);
      return null;
    }
  },
  /**
   * Updates the quantity of an item in the cart.
   *
   * @param {object} item - The cart item to update.
   * @param {number} qty - The new quantity for the item.
   * @returns {Promise<object|null>} - a promise that resolves with the cart response.
   */
  updateCartItemQuantity: (item, qty) => {
    const data = {
      id: item.key,
      quantity: qty,
    };

    fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((response) => {
        cartFunctions.dispatchCartEvent(response);
        return response;
      })
      .catch((error) => console.error("Error:", error));
  },
  /**
   * Opens the cart drawer.
   *
   * @param {Event} [e] - Optional event object.
   */
  openCart: (e) => {
    if (e) e.preventDefault();
    const event = new CustomEvent("setFullHeight");
    window.dispatchEvent(event);

    if (!window.isCartMounted) {
      dispatchEvent(new Event("mount_cart_app"));
      setTimeout(() => {}, 250);
    }
    const cartTemplate = document.getElementById("cart-template");
    if (cartTemplate && !cartTemplate.classList.contains("cart_drawer")) return;
    if (cartTemplate && !cartTemplate.classList.contains("open")) {
      cartTemplate.classList.add("open");
      //prevent body from scrolling when cart is open
      document.body.classList.add("no-scroll");
    }
  },
  /**
   * Closes the cart drawer.
   *
   * @param {Event} [e] - Optional event object.
   */

  closeCart: (e) => {
    if (e) e.preventDefault();
    const cartTemplate = document.getElementById("cart-template");
    if (cartTemplate && cartTemplate.classList.contains("open")) {
      cartTemplate.classList.remove("open");
      //allow body to scroll when cart is closed
      document.body.classList.remove("no-scroll");
    }
  },
  /**
   * Updates the cart count displayed on the page.
   *
   * @param {number} [cart_count=0] - The new cart count.
   */
  updateCartCount: (cart_count = 0) => {
    const cartCountNodes = document.querySelectorAll(".cartCount");
    const cartCount = cart_count || 0;
    if (!cartCountNodes) return;
    cartCountNodes.forEach((node) => {
      // Only update DOM if value actually changes
      if (Number(node.innerHTML) !== cartCount) {
        node.innerHTML = cartCount;
      }
      if (cartCount <= 0) {
        node.classList.add("hidden-count");
      } else {
        node.classList.remove("hidden-count");
      }
    });
  },
};

// Product Functions

const productFunctions = {
  /**
   * Extracts the product title, optionally removing gender prefixes.
   *
   * @param {string} productTitle - The full product title.
   * @param {boolean} [removeGender=false] - Whether to remove gender prefixes.
   * @returns {string} - The extracted product title.
   */
  extractProductTitle: (productTitle, removeGender = false) => {
    let title = productTitle.split("- ")[0].trim();
    if (removeGender) {
      title = title.replace("Women's", "").replace("Men's", "");
    }
    // Cache lowercased values for repeated use
    const titleLower = title.toLowerCase();
    const productTitleLower = productTitle.toLowerCase();
    if (
      titleLower.includes("soho sneaker 1") ||
      productTitleLower.includes("narrow fit")
    ) {
      title = `${title} - Narrow Fit`;
    } else if (
      titleLower.includes("alta high top")
    ) {
      title = `${title} - Regular Fit`;
    } else if (
      titleLower.includes("weekend chelsea 1") ||
      titleLower.includes("weekend 1")
    ) {
      title = title.slice(0, productTitle.lastIndexOf("1")).trim();
      title = `${title} - Original Fit`;
    } else if (titleLower.includes("astoria")) {
      title = `${title} - 17L`;
    }
    return title.trim();
  },
  extractGenderFromTitle: (productTitle) => {
    if (productTitle.toLowerCase().startsWith("women's")) {
      return "Women's";
    } else if (productTitle.toLowerCase().startsWith("men's")) {
      return "Men's";
    } else if (productTitle.toLowerCase().startsWith("kids'")) {
      return "Kids'";
    }
    return null;
  },
  /**
   * Fetches and stores reviews for a product using a web worker.
   *
   * @param {string} versionKey - The version key for the reviews API.
   * @param {number} productRemoteId - The remote ID of the product.
   * @param {Worker} fetchWorker - The web worker instance for fetching reviews.
   * @returns {Promise<object>} - A promise that resolves with the reviews data.
   */
  fetchAndStoreReviews: (versionKey, productRemoteId, fetchWorker) => {
    return new Promise((resolve, reject) => {
      if (window.Worker && fetchWorker) {
        const messageHandler = (e) => {
          if (
            e.data.action === "fetchReviewsFromJunip" &&
            e.data.productRemoteId === productRemoteId
          ) {
            if (e.data.error) {
              console.error(productTitleHandle, e.data.error);
              reject(e.data.error);
            } else {
              const reviews = e.data;
              const now = new Date();
              const expiry = now.getTime() + 1000 * 60 * 60 * 3; // 3 hours
              reviews.expiry = expiry;
              let reviewsData =
                JSON.parse(localStorage.getItem("reviewsData")) || {};

              reviewsData[productRemoteId] = reviews;
              // Wrap localStorage.setItem in a try-catch block to handle QuotaExceededError
              try {
                localStorage.setItem(
                  "reviewsData",
                  JSON.stringify(reviewsData)
                );
              } catch (e) {
                if (e.name === "QuotaExceededError") {
                  console.warn(
                    "Quota exceeded while setting reviewsData in localStorage."
                  );
                } else {
                  throw e;
                }
              }
              resolve(reviews);
            }
            // Remove this event listener once it has successfully received a message
            fetchWorker.removeEventListener("message", messageHandler);
          }
        };

        // Add the event listener
        fetchWorker.addEventListener("message", messageHandler);

        // Pass dynamic values to the worker
        fetchWorker.postMessage({
          action: "fetchReviewsFromJunip",
          productRemoteId: productRemoteId,
          versionKey: versionKey,
        });
      } else {
        reject("This browser does not support web workers.");
      }
    });
  },
  /**
   * Fetches product data using a web worker or from localStorage.
   *
   * @async
   * @param {string} handle - The product handle.
   * @param {Worker} fetchWorker - The web worker instance for fetching product data.
   * @returns {Promise<object>} - A promise that resolves with the product data.
   */
  async fetchProductFromWorker(handle, fetchWorker) {
    return new Promise((resolve, reject) => {
      const handleRemoveSlash = handle.replace("/", "");
      let data = null;
      const currency = Shopify?.currency?.active || "USD";

      // Fast path: design mode, use fetch directly
      if (Shopify.designMode || ["preview", "unpublished", "development"].includes(Shopify?.theme.role)) {
        fetch(
          `${ROOT_URL}/products/${handleRemoveSlash}?view=vue_template&currency=${currency}`
        )
          .then((res) => res.json())
          .then((product) => resolve(product))
          .catch((error) => {
            console.error("Error fetching product data:", error);
            reject(error);
          });
        return;
      }

      // Only access and parse localStorage if key exists
      const storedData = localStorage.getItem(handleRemoveSlash);
      if (storedData) {
        try {
          const decompressed = LZString.decompressFromUTF16(storedData);
          if (decompressed) {
            const storedObject = JSON.parse(decompressed);
            const now = Date.now();
            const expiryTime = storedObject.time + PRODUCT_CACHE_EXPIRY_MS; // 1 min
            if (now < expiryTime && storedObject.currency === currency) {
              data = storedObject.data;
            }
          }
        } catch (e) {
          // Ignore decompression/parse errors, will fetch fresh
        }
      }

      if (data) {
        resolve(data);
        return;
      }

      // Web worker fetch: add listener before postMessage, always remove after
      const messageHandler = (event) => {
        if (
          event.data.action === "fetchProductFromShopify" &&
          event.data.handle === handleRemoveSlash
        ) {
          const receivedData = event.data;
          if (receivedData?.product) {
            const now = Date.now();
            try {
              localStorage.setItem(
                handleRemoveSlash,
                LZString.compressToUTF16(
                  JSON.stringify({
                    time: now,
                    currency: currency,
                    data: receivedData,
                  })
                )
              );
            } catch (e) {
              if (e.name === "QuotaExceededError") {
                console.warn("Quota exceeded while setting product data in localStorage.");
              } else {
                throw e;
              }
            }
            resolve(receivedData);
          } else {
            reject("No product data received from worker");
          }
          fetchWorker.removeEventListener("message", messageHandler);
        }
      };
      let url = `${ROOT_URL}/products/${handleRemoveSlash}?view=vue_template&currency=${currency}`;

      fetchWorker.addEventListener("message", messageHandler);
      fetchWorker.postMessage({
        action: "fetchProductFromShopify",
        url: url,
        handle: handleRemoveSlash,
      });
    });
  },
  /**
   * Fetches recommended products for a given product ID.
   *
   * @async
   * @param {number} productId - The ID of the product.
   * @param {string} [intent="complementary"] - The intent for recommendations (complementary or related).
   * @returns {Promise<object|null>} - A promise that resolves with the recommended products, or null on error.
   */
  async fetchRecommendedProducts(productId, intent = "complementary") {
    try {
      const currency = Shopify?.currency?.active || "USD";
      const cacheKey = `recommended_${productId}_${intent}_${currency}`;
      // Try to get from localStorage (decompress)
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const decompressed = LZString.decompressFromUTF16(cached);
          if (decompressed) {
            return JSON.parse(decompressed);
          }
        } catch (e) {
          // If decompression fails, ignore and fetch fresh
        }
      }
      const url = `${ROOT_URL}/recommendations/products.json?product_id=${productId}&intent=${intent}&currency=${currency}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const recommendedProducts = await response.json();
      if (
        intent === "complementary" &&
        recommendedProducts?.products?.length === 0
      ) {
        return await productFunctions.fetchRecommendedProducts(
          productId,
          "related"
        );
      }
      // Compress and store in localStorage
      try {
        localStorage.setItem(
          cacheKey,
          LZString.compressToUTF16(JSON.stringify(recommendedProducts))
        );
      } catch (e) {
        if (e.name === "QuotaExceededError") {
          console.warn(
            "Quota exceeded while setting recommended products in localStorage."
          );
        } else {
          throw e;
        }
      }
      return recommendedProducts;
    } catch (error) {
      console.error("Error fetching recommended products:", error);
      return null;
    }
  },
  /**
   * Object containing size type constants.
   */
  sizeTypes: {
    STANDARD: "standard",
    WEEKEND: "weekend",
    WEEKENDCHELSEA: "classic chelsea",
    WEEKEND1: "weekend 1",
    WEEKENDCHELSEA1: "classic chelsea 1",
    KIDS: "Kids",
    BHAT: "bucket hat",
    HAT: "all weather hat",
    BEANIE: "all weather beanie",
    MIDSOCK: "mid crew socks",
    ANKSOCK: "ankle socks",
    NOSHOWSOCK: "no show socks",
    LSCREWSOCK: "lifestyle crew socks",
    CREWSOCK: "crew socks",
    TABSOCK: "tab socks",
    SLIPPERS: "sunday slipper",
    EVMOVE: "everyday move",
    EVMOVESLIPON: "everyday move slip-on",
    EVERYDAY: "everyday",
    CITYSCAPE: "cityscape",
    STORMBURST: "stormburst",
    STORMBURSTLOWTOP: "stormburst low top",
    ALTAHIGHTOP2: "alta high top",
    ALTAHIGHTOP: "alta high top - narrow fit",
    BOARDWALK: "boardwalk",
    SOHO: "soho sneaker",
    OVERCAST2: "overcast jacket 2",
    OVERCAST: "overcast",
    BASETSHIRT: "base tee",
    BASELONGSLEEVE: "base long sleeve",
    WESTERLY: "westerly",
    MARKETFLAT: "market flat",
    FMASK: "face mask",
    FORECASTGLOVES: "forecast gloves",
    FORECASTBEANIE: "forecast beanie",
    GLOVE: "waterproof glove",
    BELTBAG: "shoreline belt bag",
    ASTORIA: "astoria backpack",
    STRATUS: "stratus tote",
    COURTSIDE: "courtside classic",
    TIDAL: "tidal",
    TRINITY: "trinity rain jacket",
    UPTOWNCHELSEA: "uptown chelsea",
    COASTALHOODIE: "coastal hoodie",
    MISTBOMBER: "mist bomber",
    PACIFIC: "pacific sneaker",
    WOMEN: "Women",
    MEN: "Men",
    UNISEX: "unisex",
    WOMENS: "women",
  },
};

// GENERAL UTILS

/**
 * Creates a web worker instance from a given URL.
 *
 * @param {string} workerUrl - The URL of the web worker script.
 * @returns {Worker|null} - The web worker instance, or null if creation fails.
 */
const createWorker = (workerUrl) => {
  let worker;

  try {
    worker = new Worker(workerUrl);

    worker.onerror = function (event) {
      console.error("Error occurred in the worker:", event.message);
      event.preventDefault();
    };
  } catch (error) {
    console.error("Failed to create worker:", error);
    return null;
  }

  return worker;
};

/**
 * Dispatches a custom event to open the product details page (PDP) widget.
 *
 * @param {string} handle - The product handle.
 * @param {string} url - The URL of the product.
 */
const dispatchPdpWidgetEvent = (handle, url) => {
  const openPdpEvent = new CustomEvent("openPDPWidgetRequest", {
    detail: {
      handle: handle,
      variant_id: null,
      link_href: handle,
    },
  });
  window.dispatchEvent(openPdpEvent);
};

/**
 * Formats a price with currency and optional currency symbol display.
 *
 * @param {number} price - The price to format.
 * @param {string} [currency=CURRENCY] - The currency code.
 * @param {boolean} [showCurrency=true] - Whether to display the currency symbol.
 * @returns {string|null} - The formatted price string, or null if price is invalid.
 */
const formatPrice = (price, currency = CURRENCY, showCurrency = true) => {
  if (price === 0) return `FREE`;
  if (!price) return null;
  let adjustedPrice = price / 100;

  try {
    let fractionDigits = ["JPY", "KRW"].includes(currency)
      ? 0
      : Number.isInteger(adjustedPrice)
        ? 0
        : 2;
    // Format the price as a number with two decimal places
    let formattedNumber = new Intl.NumberFormat(navigator.language || "en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(adjustedPrice);

    // If the currency is CAD, replace CA$ with $
    if (currency === "CAD") {
      formattedNumber = formattedNumber.replace("CA$", "$");
    }

    // Manually construct the formatted price string with the currency code
    // Never show the currency if it's USD or CAD
    return showCurrency && !["CAD", "USD"].includes(currency)
      ? `${formattedNumber} ${currency}`
      : `${formattedNumber}`;
  } catch (error) {
    console.error(`Error formatting price: ${error.message}`);
    return `$${adjustedPrice.toFixed(2)} ${currency}`;
  }
};

/**
 * Gets the discount price from a tag, applying percentage or fixed amount discounts.
 *
 * @param {string} tag - The discount tag (e.g., "discount-10").
 * @param {number} price - The original price.
 * @param {string} type - The discount type ("percentage" or "fixed_amount").
 * @param {string} [currency=CURRENCY] - The currency code.
 * @param {boolean} [showCurrency=false] - Whether to display the currency symbol.
 * @param {number} [rate=1] - The exchange rate.
 * @returns {string|null} - The discounted price string, or null if discount is invalid.
 */
const getDiscountPriceFromTag = (
  tag,
  price,
  type,
  currency = CURRENCY,
  showCurrency = false,
  rate = 1
) => {
  if (!tag || !price || !type) return null;
  let discountTagValue = parseInt(tag.split("-")[1]);
  if (!discountTagValue || isNaN(discountTagValue)) return null;
  // return only the value if not USD or CAD
  if (currency !== "USD" && currency !== "CAD" && type === "fixed_amount")
    discountTagValue = discountTagValue * rate;
  // return the price with discount applied
  let upsellPrice = null;
  if (type === "percentage") {
    upsellPrice = price - price * (discountTagValue / 100);
    upsellPrice = Math.round(upsellPrice / 100) * 100;
  } else if (type === "fixed_amount") {
    upsellPrice = price - discountTagValue * 100;
  }
  return formatPrice(upsellPrice, currency, showCurrency);
};

/**
 * Displays a toast message on the page.
 *
 * @param {string} message - The message to display.
 * @param {number} [timeout=3000] - The display timeout in milliseconds.
 * @param {string} [errorClass=null] - Optional CSS class for error messages.
 */
const toastMessage = (message, timeout = 3000, errorClass = null) => {
  if (!message || message === "") return;
  const toastMessage = document.createElement("div");
  toastMessage.classList.add("cart_toast_message");
  if (errorClass) {
    toastMessage.classList.add(errorClass);
  }
  toastMessage.innerHTML = message;
  document.body.appendChild(toastMessage);
  setTimeout(() => {
    toastMessage.remove();
  }, timeout);
};

/**
 * Checks if a cookie exists.
 *
 * @param {string} cname - The name of the cookie.
 * @returns {boolean|null} - True if the cookie exists, false otherwise.
 */

const hasCookie = (cname) => {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");

  for (let c of ca) {
    let trimmedCookie = c.trim();
    if (trimmedCookie.startsWith(name)) {
      let cookieValue = trimmedCookie.substring(name.length);
      if (cookieValue) {
        return true;
      }
    }
  }
  return null;
};

/**
 * Returns the value of a cookie by its name.
 *
 * @param {string} cname - The name of the cookie.
 * @returns {string|null} - The cookie value, or null if not found.
 */
const getCookie = (cname) => {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");

  for (let c of ca) {
    let trimmedCookie = c.trim();
    if (trimmedCookie.startsWith(name)) {
      return trimmedCookie.substring(name.length);
    }
  }
  return null;
};

/**
 * Generates an image URL with a specified size.
 *
 * @param {string} src - The original image URL.
 * @param {string} size - The desired image size.
 * @returns {string|null} - The modified image URL, or null if src is invalid.
 */
const imgURL = (src, size) => {
  if (!src || typeof src !== "string") return null;
  const url = new URL(src, window.location.origin); // Use window.location.origin for relative URLs
  url.searchParams.set('width', size);
  return url.toString();
};

/**
 * Renders a text node into HTML.
 *
 * @param {object} textNode - The text node to render.
 * @returns {string} - The rendered HTML string.
 */
const renderRichTextObject = (richTextObject) => {
  if (!richTextObject) {
    return "";
  }
  if (typeof richTextObject === "string") {
    return `<p>${richTextObject}</p>`;
  }
  let html = "";
  richTextObject.children.forEach((node) => {
    switch (node.type) {
      case "text":
        html += renderText(node);
        break;
      case "paragraph":
        html += `<p>${renderRichTextObject(node)}</p>`;
        break;
      case "heading":
        html += `<h${node.level}>${renderRichTextObject(node)}</h${
          node.level
        }>`;
        break;
      case "list":
        html += `<${
          node.listType === "ordered" ? "ol" : "ul"
        }>${renderRichTextObject(node)}</${
          node.listType === "ordered" ? "ol" : "ul"
        }>`;
        break;
      case "list-item":
        html += `<li>${renderRichTextObject(node)}</li>`;
        break;
      case "link":
        html += `<a href="${node.url}"${
          node.target ? ` target="${node.target}"` : ""
        }>${renderRichTextObject(node)}</a>`;
        break;
      case "richtext":
        html += renderRichTextObject(node);
        break;
      default:
        break;
    }
  });
  return html;
};

/**
 * Renders a text node into HTML.
 *
 * @param {object} textNode - The text node to render.
 * @returns {string} - The rendered HTML string.
 */
const renderText = (textNode) => {
  let html = textNode.value;
  if (textNode.italic) {
    html = `<em>${html}</em>`;
  }
  if (textNode.bold) {
    html = `<strong>${html}</strong>`;
  }
  if (textNode.underline) {
    html = `<u>${html}</u>`;
  }
  if (textNode.strikethrough) {
    html = `<del>${html}</del>`;
  }
  return html;
};

/**
 * Throttles a function to limit its execution rate.
 *
 * @param {function} func - The function to throttle.
 * @param {number} limit - The time limit in milliseconds.
 * @returns {function} - The throttled function.
 */
function throttle(func, limit) {
  let isRunning = false;
  let timer;

  return function (...args) {
    const context = this;
    if (!isRunning) {
      func.apply(context, args);
      isRunning = true;
    } else {
      timer = setTimeout(function () {
        func.apply(context, args);
        isRunning = false; // Reset lastRan after timeout
        clearTimeout(timer); // Clear the timer
      }, limit);
    }
  };
}

/**
 * Debounces a function to delay its execution until after a specified time.
 *
 * @param {function} func - The function to debounce.
 * @param {number} [timeout=300] - The delay time in milliseconds.
 * @returns {function} - The debounced function.
 */
function debounce(func, timeout = 300) {
  let timer;
  return function (...args) {
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(context, args);
    }, timeout);
  };
}

/**
 * Pushes an event to the Google Tag Manager dataLayer.
 *
 * @param {object} eventData - An object containing the event name and data.
 * @param {string} eventData.event - The name of the event.
 * @param {object} [eventData.data] - Optional data associated with the event.
 *
 * Example Usage:
 * pushDataLayerEvent({
 * event: 'productView',
 * data: {
 * productId: '12345',
 * productName: 'Example Product',
 * category: 'Shoes'
 * }
 * });
 */
function pushDataLayerEvent(eventData) {
  if (typeof eventData !== "object" || !eventData.event) {
    console.error(
      'Invalid eventData. Must be an object with an "event" property.'
    );
    return;
  }

  try {
    window.dataLayer = window.dataLayer || [];

    const eventId = window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : String(Math.random()).replace("0.", "");

    eventData.data = {
      ...eventData?.data,
      event_id: eventId,
    };

    window.dataLayer.push(eventData);
  } catch (error) {
    console.error("Error pushing to dataLayer:", error);
  }
}

function roundToDecimalPlaces(num, decimalPlaces) {
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(num * factor) / factor;
}

/**
 * Copy text of element to clipboard by ID.
 * @param {string} elementId - The ID of the element to copy text from.
 * @return {Promise<void>} - A promise that resolves when the text is copied.
 * @throws {Error} - Throws an error if the element is not found or if copying fails.
 * @example
 * copyTextToClipboard("myElementId")
 *   .then(() => {
 *     console.log("Text copied to clipboard");
 *   })
 *   .catch((error) => {
 *     console.error("Error copying text:", error);
 *   });
 */
function copyTextToClipboard(elementId) {
  return new Promise((resolve, reject) => {
    const element = document.getElementById(elementId);
    if (!element) {
      reject(new Error(`Element with ID "${elementId}" not found.`));
      return;
    }

    const text = element.innerText || element.value;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // show toast message
        toastMessage("Text copied to clipboard", 2000);
        resolve();
      })
      .catch((error) => {
        reject(new Error("Failed to copy text: " + error));
      });
  });
}

window.themeUtils = {
  cartFunctions,
  productFunctions,
  general: {
    createWorker,
    copyTextToClipboard,
    dispatchPdpWidgetEvent,
    formatPrice,
    toastMessage,
    getDiscountPriceFromTag,
    hasCookie,
    getCookie,
    imgURL,
    renderRichTextObject,
    throttle,
    debounce,
    pushDataLayerEvent,
    roundToDecimalPlaces,
  },
};

// Other functions

/**
 * Changes the currency of the cart.
 *
 * @param {string} currency - The currency to change to.
 */
const changeCurrency = (currency) => {
  if (!currency) return;

  fetch("/cart/update", {
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
    },
    body: new URLSearchParams({
      currency: currency,
    }),
  })
    .then((res) => res.status)
    .then((status) => {
      if (status === 200) {
        window.location.reload();
      } else {
        throw new Error("Error changing currency");
      }
    })
    .catch((err) => console.error(err));
};

let currentCurrency = null;

if (Shopify) {
  if (!Shopify.Checkout) {
    currentCurrency = Shopify.currency.active;
  }
}

const currentShop = Shopify ? Shopify.shop : null;
if (currentShop === VESSI_GLOBAL_STOREFRONT && currentCurrency === "CAD") {
  changeCurrency("USD");
}
